/**
 * Table Pagination
 *
 * Numbered page controls (1, 2, 3, …) with Previous/Next, windowed around
 * the current page so it stays usable at any page count (e.g. 500 pages for
 * 50k rows at 100/page) — always shows the first and last page plus a
 * window around the current one, collapsing the rest into an ellipsis.
 *
 * @fileoverview Reusable numbered pagination control
 */

import React, { useState } from 'react';
import { HStack, Button, Text, IconButton } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';

export interface TablePaginationProps {
  /** Current page, 1-indexed. */
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Disables all controls while a page fetch is in flight. */
  isLoading?: boolean;
  /** How many page numbers to show on each side of the current page. */
  siblingCount?: number;
  /**
   * Called before navigating, so a caller with an in-progress inline edit
   * (e.g. a transaction row mid-keystroke) can flush it first instead of
   * silently losing it when the row unmounts on page change. Resolving
   * false aborts the page change — used when the flush itself failed and
   * left something open for the user to retry (see TransactionRow's
   * onEditingActiveChange). Omit for callers with nothing to flush (e.g.
   * admin tables) — navigation proceeds immediately, as before.
   */
  onBeforePageChange?: () => Promise<boolean>;
}

/**
 * Computes the sequence of page numbers/ellipsis markers to render.
 * Always includes page 1 and totalPages; fills in siblingCount pages on
 * each side of `page`; everything else collapses to a single '…' marker
 * per gap (never more than one ellipsis on each side).
 */
function buildPageWindow(
  page: number,
  totalPages: number,
  siblingCount: number,
): Array<number | '…'> {
  const totalNumbers = siblingCount * 2 + 5; // first, last, current, 2 ellipses
  if (totalPages <= totalNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(page - siblingCount, 1);
  const rightSibling = Math.min(page + siblingCount, totalPages);

  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < totalPages - 1;

  const pages: Array<number | '…'> = [1];

  if (showLeftEllipsis) {
    pages.push('…');
  } else {
    for (let p = 2; p < leftSibling; p++) pages.push(p);
  }

  for (let p = leftSibling; p <= rightSibling; p++) {
    if (p !== 1 && p !== totalPages) pages.push(p);
  }

  if (showRightEllipsis) {
    pages.push('…');
  } else {
    for (let p = rightSibling + 1; p < totalPages; p++) pages.push(p);
  }

  pages.push(totalPages);
  return pages;
}

export const TablePagination: React.FC<TablePaginationProps> = ({
  page,
  totalPages,
  onPageChange,
  isLoading = false,
  siblingCount = 1,
  onBeforePageChange,
}) => {
  const [isFlushing, setIsFlushing] = useState(false);

  if (totalPages <= 1) return null;

  const pageWindow = buildPageWindow(page, totalPages, siblingCount);
  const controlsDisabled = isLoading || isFlushing;

  const goTo = async (target: number) => {
    if (target === page) return;
    if (!onBeforePageChange) {
      onPageChange(target);
      return;
    }
    setIsFlushing(true);
    try {
      const canProceed = await onBeforePageChange();
      if (canProceed) onPageChange(target);
    } finally {
      setIsFlushing(false);
    }
  };

  return (
    <HStack spacing={1}>
      <IconButton
        aria-label="Previous page"
        icon={<ChevronLeftIcon />}
        size="sm"
        variant="outline"
        onClick={() => goTo(Math.max(1, page - 1))}
        isDisabled={page <= 1 || controlsDisabled}
      />
      {pageWindow.map((entry, i) =>
        entry === '…' ? (
          <Text key={`ellipsis-${i}`} px={1} color="gray.500" fontSize="sm">
            …
          </Text>
        ) : (
          <Button
            key={entry}
            size="sm"
            minW="8"
            variant={entry === page ? 'solid' : 'outline'}
            colorScheme={entry === page ? 'blue' : 'gray'}
            onClick={() => goTo(entry)}
            isDisabled={controlsDisabled}
            aria-current={entry === page ? 'page' : undefined}
          >
            {entry}
          </Button>
        ),
      )}
      <IconButton
        aria-label="Next page"
        icon={<ChevronRightIcon />}
        size="sm"
        variant="outline"
        onClick={() => goTo(Math.min(totalPages, page + 1))}
        isDisabled={page >= totalPages || controlsDisabled}
      />
    </HStack>
  );
};

export default TablePagination;
